import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PhoneCall, Mic, ArrowRight } from "lucide-react";
import api from "@/lib/api";

/**
 * Live social-proof strip showing how many visitors Zero (our AI voice agent)
 * has actually spoken with. Hides itself gracefully when there isn't enough
 * real data yet (< 5 completed calls) so we never show fake numbers.
 */
export default function VoiceSocialProof() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/voice/public-stats")
      .then((r) => { if (!cancelled) setStats(r.data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  if (!stats || stats.total < 5) return null;

  const minutes = Math.max(1, Math.round(stats.avg_duration_sec / 60));
  const langs = (stats.languages || []).slice(0, 3).join(" · ");

  return (
    <section className="py-10" data-testid="voice-social-proof">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <Link
          to="/contact"
          className="group block relative overflow-hidden rounded-2xl border border-[#3B82F6]/25 bg-[#3B82F6]/[0.04] hover:border-[#3B82F6]/60 transition-colors"
        >
          {/* subtle waveform background */}
          <div className="absolute inset-0 pointer-events-none opacity-[0.08]" aria-hidden="true">
            <div className="h-full w-full" style={{
              backgroundImage: "radial-gradient(circle at 12% 50%, rgba(59,130,246,0.6) 0, transparent 30%), radial-gradient(circle at 88% 50%, rgba(34,211,238,0.5) 0, transparent 35%)",
            }}/>
          </div>

          <div className="relative grid md:grid-cols-[auto,1fr,auto] items-center gap-6 p-6 md:p-7">
            {/* Pulsing mic */}
            <div className="relative shrink-0">
              <span className="absolute inset-0 rounded-2xl bg-[#3B82F6]/30 animate-ping"/>
              <div className="relative w-14 h-14 rounded-2xl bg-gradient-to-br from-[#3B82F6] to-[#60A5FA] flex items-center justify-center shadow-[0_8px_24px_-6px_rgba(59,130,246,0.6)]">
                <Mic size={22} className="text-white"/>
              </div>
            </div>

            {/* Live stats */}
            <div>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#3B82F6] font-semibold">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3B82F6] animate-pulse"/>
                Live · AI voice agent
              </div>
              <div className="mt-1.5 text-white text-lg md:text-xl font-semibold leading-snug">
                Zero has spoken with{" "}
                <span data-testid="voice-stat-total" className="text-[#3B82F6] font-extrabold tabular-nums">{stats.total.toLocaleString()}</span>{" "}
                visitors
                {stats.this_week > 0 && (
                  <>
                    {" "}— <span data-testid="voice-stat-week" className="text-[#22D3EE] font-extrabold tabular-nums">{stats.this_week}</span> this week
                  </>
                )}
              </div>
              <div className="mt-1.5 text-[#B6B0C7] text-sm">
                Drop your number and Zero calls you in seconds. Avg call ~{minutes} min{langs ? <> · speaks {langs.toUpperCase()}</> : null}.
              </div>
            </div>

            {/* CTA */}
            <div className="md:justify-self-end">
              <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/[0.06] border border-white/10 text-white text-sm font-semibold group-hover:border-[#3B82F6]/50 group-hover:bg-[#3B82F6]/10 transition-colors">
                <PhoneCall size={14}/> Talk to Zero
                <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform"/>
              </span>
            </div>
          </div>
        </Link>
      </div>
    </section>
  );
}
